/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import { Reducer, AnyAction } from "redux";

import {
  GET_ALL_OWNS,
  GET_CURRENT_OWN,
  UPDATE_OWN,
  SET_OWN_DATA,
  SET_OWN_TEST,
  GET_OWN_TEST,
  CHECKOUT_SUCCESS,
} from "client/actions/types";

export type AllOwnsEntry = {
  currentLec: string | undefined;
  currentVPos: number;
  dateAcquired: Date;
};

export type AllOwns = {
  [course: string]: AllOwnsEntry;
};

type dbTestResult = {
  _id: string;
  rand?: number[];
  userAnsBool?: boolean[];
  userAnsNum?: number[];
  userAnsStr?: string;
};

export type OwnEntry =
  | {
      course: string;
      currentLec: string | undefined;
      currentVPos: number | undefined;
      lecData: {
        [lecture: string]: {
          videoPos?: number;
          progress?: number;
          liked?: boolean;
        };
      };
    }
  | undefined;

export type TestData = {
  [infobox: string]: {
    dateTaken: Date;
    timeSpent: number;
    done: boolean;
    points: number;
    tests: dbTestResult[];
  };
};

export type OwnState = {
  readonly owns: AllOwns | undefined;
  readonly currentOwn: OwnEntry | undefined;
  readonly testData: TestData;
  readonly currentTestId: string;
};

const ownReducerDefaultState: OwnState = {
  owns: undefined,
  currentOwn: undefined,
  testData: {},
  currentTestId: "",
};

export const ownReducer: Reducer<OwnState, AnyAction> = (
  state = ownReducerDefaultState,
  action: AnyAction,
) => {
  switch (action.type) {
    case SET_OWN_DATA: {
      if (!state.owns) return state;

      // update both owns and currentOwn
      const { cid, lid, props } = action.payload;
      var own = state.owns[cid];
      if ("videoPos" in props) {
        own.currentLec = lid;
        own.currentVPos = props.videoPos;
      }

      let currentOwn: OwnEntry = state.currentOwn;
      if (currentOwn && currentOwn.course === cid) {
        if (currentOwn.lecData[lid]) {
          Object.assign(currentOwn.lecData[lid], props);
        } else {
          currentOwn.lecData[lid] = props;
        }
        if ("videoPos" in props) {
          currentOwn.currentLec = lid;
          currentOwn.currentVPos = props.videoPos;
        }
      }
      return { ...state, owns: { ...state.owns, [cid]: own }, currentOwn };
    }

    case GET_ALL_OWNS:
      return { ...state, owns: action.payload };

    case GET_CURRENT_OWN:
      return { ...state, currentOwn: action.payload };

    case SET_OWN_TEST:
    case GET_OWN_TEST: {
      const { bid, data } = action.payload;
      if (data === null) {
        // data has been loaded. Just switch currentTestId.
        return { ...state, currentTestId: bid };
      } else {
        let testData = state.testData;
        testData[bid] = data;
        return { ...state, testData, currentTestId: bid };
      }
    }

    case UPDATE_OWN: {
      if (!state.owns) return state;

      const { cid, props } = action.payload;
      var own = state.owns[cid];
      Object.assign(own, props);

      let currentOwn: OwnEntry = state.currentOwn;
      if (currentOwn && currentOwn.course === cid) {
        Object.assign(currentOwn, props);
      }

      return { ...state, owns: { ...state.owns, [cid]: own }, currentOwn };
    }

    case CHECKOUT_SUCCESS: {
      const newOwns = {};
      action.payload.forEach((own: any) => {
        const { course, ...rest } = own;
        newOwns[own.course] = { ...rest };
      });
      return { ...state, owns: { ...state.owns, ...newOwns } };
    }

    default:
      return state;
  }
};
